/**
 * GENERATED by `npm run import:pricing` — do not edit by hand.
 *
 * Default service prices from data/pricing/QuotyLab_US_Pricing_Database.xlsx
 * (sheet MASTER, column M "Recommended Price (numeric)", USD).
 *
 * 73 of 73 workbook rows are integrated: 56 in the unit the app already
 * sold them in, 8 with the catalog unit corrected to the workbook's, and 9 whose
 * default is only true with the workbook pricing model shipped beside it. Every
 * other service keeps the `basePrice` authored in its template file. The
 * decision for every row is recorded in data/pricing/reconciliation.md.
 */
import type { Money } from '@/types';

export const WORKBOOK_SOURCE = {
  file: 'data/pricing/QuotyLab_US_Pricing_Database.xlsx',
  sha256: '99da2d01c06da512ffd251a4e046c8d269ea9e43e54bda77d4ca83db1c9f9db0',
  sheet: 'MASTER',
  priceColumn: 'M',
  currency: 'USD',
} as const;

/** Service definition id → default price, exactly as the workbook states it. */
export const WORKBOOK_DEFAULT_PRICES: Readonly<Record<string, Money>> = {
  'car-detailing.exterior-wash': 55, // MASTER!M2
  'car-detailing.foam-wash': 65, // MASTER!M3
  'car-detailing.engine-bay-cleaning': 95, // MASTER!M4
  'car-detailing.headlight-restoration': 99, // MASTER!M5
  'car-detailing.tyre-dressing': 15, // MASTER!M6
  'car-detailing.interior-cleaning': 69, // MASTER!M7
  'car-detailing.steam-cleaning': 89, // MASTER!M8
  'car-detailing.seat-shampoo': 25, // MASTER!M9
  'car-detailing.wax-polish': 119, // MASTER!M10
  'car-detailing.glass-coating': 69, // MASTER!M11
  'car-detailing.paint-correction': 595, // MASTER!M12
  'car-detailing.ceramic-coating': 1295, // MASTER!M13
  'pressure-washing.driveway-wash': 0.25, // MASTER!M14
  'pressure-washing.patio-wash': 0.3, // MASTER!M15
  'pressure-washing.deck-cleaning': 0.4, // MASTER!M16
  'pressure-washing.sidewalk-cleaning': 0.18, // MASTER!M17
  'pressure-washing.concrete-cleaning': 0.22, // MASTER!M18
  'pressure-washing.house-wash': 0.3, // MASTER!M19
  'pressure-washing.fence-cleaning': 1.75, // MASTER!M20
  'pressure-washing.roof-soft-wash': 0.35, // MASTER!M21
  'pressure-washing.gutter-cleaning': 1.25, // MASTER!M22
  'pressure-washing.concrete-sealing': 1.75, // MASTER!M23
  'plumbing.leak-repair': 325, // MASTER!M24
  'plumbing.pipe-replacement': 10, // MASTER!M25
  'plumbing.tap-installation': 185, // MASTER!M26
  'plumbing.toilet-installation': 325, // MASTER!M27
  'plumbing.bathroom-fitting': 6500, // MASTER!M28
  'plumbing.kitchen-plumbing': 4800, // MASTER!M29
  'plumbing.drain-cleaning': 225, // MASTER!M30
  'plumbing.water-heater-service': 145, // MASTER!M31
  'plumbing.emergency-visit': 195, // MASTER!M32
  'electrician.light-installation': 185, // MASTER!M33
  'electrician.fan-installation': 265, // MASTER!M34
  'electrician.switch-installation': 145, // MASTER!M35
  'electrician.socket-installation': 185, // MASTER!M36
  'electrician.house-wiring': 8, // MASTER!M37
  'electrician.generator-wiring': 1250, // MASTER!M38
  'electrician.mcb-replacement': 185, // MASTER!M39
  'electrician.electrical-inspection': 165, // MASTER!M40
  'electrician.emergency-visit': 195, // MASTER!M41
  'hvac.general-service': 145, // MASTER!M42
  'hvac.gas-refill': 265, // MASTER!M43
  'hvac.coil-cleaning': 185, // MASTER!M44
  'hvac.installation': 1850, // MASTER!M45
  'hvac.uninstallation': 185, // MASTER!M46
  'hvac.compressor-repair': 2300, // MASTER!M47
  'hvac.gas-leak-repair': 650, // MASTER!M48
  'hvac.inspection': 165, // MASTER!M49
  'lawn-care.lawn-mowing': 55, // MASTER!M50
  'lawn-care.grass-cutting': 95, // MASTER!M51
  'lawn-care.weed-removal': 85, // MASTER!M52
  'lawn-care.hedge-trimming': 3.5, // MASTER!M53
  'lawn-care.tree-trimming': 475, // MASTER!M54
  'lawn-care.planting': 55, // MASTER!M55
  'lawn-care.mulching': 95, // MASTER!M56
  'lawn-care.garden-cleaning': 65, // MASTER!M57
  'home-cleaning.kitchen-cleaning': 100, // MASTER!M58
  'home-cleaning.bathroom-cleaning': 110, // MASTER!M59
  'home-cleaning.bedroom-cleaning': 50, // MASTER!M60
  'home-cleaning.window-cleaning': 12, // MASTER!M61
  'home-cleaning.deep-cleaning': 325, // MASTER!M62
  'home-cleaning.carpet-cleaning': 0.35, // MASTER!M63
  'home-cleaning.sofa-cleaning': 185, // MASTER!M64
  'home-cleaning.move-in-cleaning': 375, // MASTER!M65
  'salon-spa.hair-cut': 45, // MASTER!M66
  'salon-spa.hair-wash': 20, // MASTER!M67
  'salon-spa.hair-colour': 115, // MASTER!M68
  'salon-spa.facial': 95, // MASTER!M69
  'salon-spa.waxing': 45, // MASTER!M70
  'salon-spa.beard-trim': 25, // MASTER!M71
  'salon-spa.shaving': 50, // MASTER!M72
  'salon-spa.hair-spa': 85, // MASTER!M73
  'salon-spa.massage': 95, // MASTER!M74
};

/**
 * Service definition id → the workbook pricing model its default is only true with.
 * Composed from MASTER columns L, N, O and K: the column N basis is attached to the column L
 * default alone, and any further price term keeps its own words. Shown to the owner, never printed.
 */
export const WORKBOOK_PRICING_NOTES: Readonly<Record<string, string>> = {
  'plumbing.drain-cleaning': "Workbook default: $225 (standard) per drain. Add a separate $650 hydro-jet + camera line. Tiered: snake/auger vs hydro-jetting; camera inspection as an add-on. Minimum: $75-$250 service call.", // MASTER row 30
  'hvac.gas-refill': "Workbook default: $265 (up to 3 lb R-410A) per recharge. Plus $70/lb thereafter. Base charge covering the minimum poundage + per pound. Minimum: 3 lb minimum common.", // MASTER row 43
  'hvac.installation': "Workbook default: $1,850 labour-only per zone. Per zone / per tonnage, labour-only, equipment quoted separately.", // MASTER row 45
  'lawn-care.lawn-mowing': "Workbook default: $55 per visit (up to 5,000 sq ft). Then $0.03 / sq ft. Per-visit base with a per-sq-ft escalator; recurring-schedule discount. Minimum: $40-$50 per visit.", // MASTER row 50
  'lawn-care.grass-cutting': "Workbook default: $95 per visit. Reposition as 'First Cut / Overgrown Lawn' with a per-visit surcharge, or delete as a duplicate. Minimum: $40-$50.", // MASTER row 51
  'home-cleaning.deep-cleaning': "Workbook default: $325 per property (up to ~2,000 sq ft). Per sq ft or per bedroom/bathroom count, not a single flat price.", // MASTER row 62
  'home-cleaning.move-in-cleaning': "Workbook default: $375 per property (up to ~2,000 sq ft). Per sq ft or per bedroom/bathroom count.", // MASTER row 65
  'salon-spa.waxing': "Workbook default: $45 (bikini line / half leg) per area. Per body area — this must become a menu, not one price.", // MASTER row 70
  'salon-spa.massage': "Workbook default: $95 (60-min) per session. Tiered by duration (30/60/90/120 min) and modality.", // MASTER row 74
};
